/**
 * Rules the public booking form and the booking endpoint must agree on.
 *
 * The endpoint is public, so anything the form enforces has to be enforced
 * again on the server. Keeping the rule in one place is what makes "again"
 * mean "identically".
 */

/**
 * True when the clinic insists on a message and the patient has not written one.
 *
 * Only applies to public bookings: staff booking on a patient's behalf have the
 * person in front of them, so the field stays optional for them.
 */
export function messageRequirementUnmet(args: {
  publicBooking: boolean;
  clinicRequiresMessage: boolean;
  message?: string | null;
}): boolean {
  if (!args.publicBooking) return false;
  if (!args.clinicRequiresMessage) return false;
  return !args.message?.trim();
}

/**
 * The booking endpoint's error when the chosen time is no longer free — taken
 * by another appointment or, for public bookings, blocked — while the patient
 * was filling in the form. The form matches on it to offer a fresh choice.
 */
export const SLOT_TAKEN = "Termín je už obsadený";

/**
 * Longest confirmation text a clinic may set. Enough for a cancellation policy
 * of a few paragraphs; anything longer is a document, not a checkbox label.
 */
export const CONSENT_TEXT_MAX_LENGTH = 2000;

/**
 * The clinic's confirmation text as stored and compared: trimmed, and null when
 * there is nothing left — an empty text means no checkbox, not an empty one.
 */
export function normalizeConsentText(text?: string | null): string | null {
  const trimmed = text?.trim();
  return trimmed ? trimmed : null;
}

/** The checkbox is a Plus feature: every plan above Solo has it. */
export function consentTextAvailable(tier?: string | null): boolean {
  return !!tier && tier !== "solo";
}

/**
 * The text the public form shows and the endpoint enforces. A clinic that
 * drops to Solo keeps its text saved, so moving back up restores it, but it no
 * longer applies.
 */
export function effectiveConsentText(clinic?: {
  tier?: string | null;
  booking_consent_text?: string | null;
} | null): string | null {
  if (!consentTextAvailable(clinic?.tier)) return null;
  return normalizeConsentText(clinic?.booking_consent_text);
}

/** The patient did not tick the clinic's checkbox. */
export const CONSENT_REQUIRED = "CONSENT_REQUIRED";
/**
 * The patient ticked a text the clinic has since changed. Refused rather than
 * accepted, because the appointment records what was agreed to and the patient
 * never saw the new wording.
 */
export const CONSENT_CHANGED = "CONSENT_CHANGED";

/**
 * Why a booking fails the clinic's confirmation checkbox, or null when it
 * passes.
 *
 * `confirmedText` is the text the form showed next to the ticked box, sent back
 * as it was shown; a form with the box unticked sends nothing. Like the message
 * rule, it binds public bookings only.
 */
export function consentProblem(args: {
  publicBooking: boolean;
  clinicConsentText?: string | null;
  confirmedText?: string | null;
}): typeof CONSENT_REQUIRED | typeof CONSENT_CHANGED | null {
  if (!args.publicBooking) return null;
  const required = normalizeConsentText(args.clinicConsentText);
  if (!required) return null;
  const confirmed = normalizeConsentText(args.confirmedText);
  if (!confirmed) return CONSENT_REQUIRED;
  return confirmed === required ? null : CONSENT_CHANGED;
}
